/*
 * Copyright (c) 2023 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, August 10th 2023, 3:21:57 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { ContextTagSelectorProps } from "common/components/context-tags";
import { useNavigateToFormBuilder as _useNavigateToFormBuilder } from "form-builder/hooks";
import { Portal } from "lib/schemas";

interface IFormBuilderContext {
  portalIds: Portal["id"][];
  formsAPIListFilter?: { [key: string]: string | number };
  contextTagsAPIFilters?: ContextTagSelectorProps["contextTagsAPIFilters"];
  useNavigateToFormBuilder?: typeof _useNavigateToFormBuilder;
}

const FormBuilderContext = React.createContext<IFormBuilderContext>({
  useNavigateToFormBuilder: _useNavigateToFormBuilder,
  portalIds: null,
  formsAPIListFilter: null,
});

type FormBuilderProviderProps = IFormBuilderContext & {
  children: React.ReactNode;
};

/**
 * This FormBuilderProvider is used to provide the context for the form builder. Our use case for it is for dependency injection
 * into the form builder. Especially for the useNavigateToFormBuilder hook.
 *
 * The form builder can run without this provider perfectly well as defaults are provided for the context in useFormBuilderContext.
 *
 */
export const FormBuilderProvider = ({ children, ...props }: FormBuilderProviderProps) => {
  return <FormBuilderContext.Provider value={props}>{children}</FormBuilderContext.Provider>;
};

export const useFormBuilderContext = () => {
  const formBuilderContext = React.useContext(FormBuilderContext);

  return formBuilderContext;
};
